import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { format, formatDistanceToNow } from 'date-fns';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import { actionRunOptions } from '@/hooks/query-options';
import { hasPermission } from '@/lib/permissions';
import { cn } from '@/utils/cn';
import { toTitleCase } from '@/utils/to-title-case';
import { PageContent } from '@/components/page-content';
import { Badge } from '@/components/ui/badge';
import { RunResult } from '@/components/actions/run-result';
import { actionVisual, ACCENT_TILE } from '@/components/actions/action-visuals';
import {
  RUN_STATUS_VARIANT,
  RUN_TRIGGER_META,
  runDurationLabel,
} from '@/components/actions/run-meta';

// Full-page view of one action run (the Activity feed's detail) — same
// convention as the Company Profile detail: the `your-actions_.activity_`
// prefix escapes the tabbed /actions/your-actions layout.
export const Route = createFileRoute(
  '/_auth/_side-layout/actions/your-actions_/activity_/$runId',
)({
  component: RunDetailPage,
  // Same guard as the your-actions layout this route escapes.
  beforeLoad: async ({ context }) => {
    if (!hasPermission(context.userRole, { organization: ['update'] })) {
      throw redirect({ to: '/' });
    }
  },
  // Warm the run query. A missing/foreign id 404s — swallow it here and let
  // the component render its not-found state instead of a route-level error.
  loader: ({ context: { queryClient }, params }) =>
    queryClient.ensureQueryData(actionRunOptions(params.runId)).catch(() => null),
});

function RunDetailPage() {
  const { runId } = Route.useParams();
  const { data: run, isLoading } = useQuery(actionRunOptions(runId));

  if (isLoading) {
    return (
      <PageContent>
        <p className="text-muted-foreground text-sm">Loading…</p>
      </PageContent>
    );
  }

  if (!run) {
    return (
      <PageContent>
        <div className="space-y-3">
          <p className="text-muted-foreground text-sm">
            This run doesn't exist (or belongs to another organization).
          </p>
          <BackToActivity />
        </div>
      </PageContent>
    );
  }

  const { Icon, accent } = actionVisual(run.action_key);
  const trigger = RUN_TRIGGER_META[run.trigger_source] ?? {
    icon: RUN_TRIGGER_META.manual!.icon,
    label: 'Manual',
  };
  const TriggerIcon = trigger.icon;

  return (
    <PageContent>
      <div className="mx-auto w-full max-w-3xl space-y-6">
        <BackToActivity />

        {/* Header band — mirrors the action dialogs: tile, name, pipe-separated meta. */}
        <div className="space-y-3 border-b pb-5">
          <div className="flex items-center gap-3">
            <span
              className={cn(
                'flex size-11 shrink-0 items-center justify-center rounded-md ring-1 ring-inset [&_svg]:size-5',
                ACCENT_TILE[accent],
              )}
            >
              <Icon />
            </span>
            <div className="min-w-0">
              <h1 className="truncate text-lg font-semibold">
                {run.action_name ?? run.action_label}
              </h1>
              {run.action_name && (
                <p className="text-muted-foreground truncate text-sm">
                  {run.action_label}
                </p>
              )}
            </div>
            <Badge
              size="sm"
              variant={RUN_STATUS_VARIANT[run.status]}
              className="ml-auto shrink-0"
            >
              {toTitleCase(run.status)}
            </Badge>
          </div>
          <div className="text-muted-foreground flex flex-wrap items-center gap-2.5 text-sm">
            <span className="inline-flex items-center gap-1.5 [&_svg]:size-3.5">
              <TriggerIcon />
              {trigger.label}
            </span>
            <span className="bg-primary/40 h-3 w-px" />
            <span
              title={format(new Date(run.started_at), 'MMM d, yyyy h:mm a')}
            >
              {formatDistanceToNow(new Date(run.started_at), {
                addSuffix: true,
              })}
            </span>
            <span className="bg-primary/40 h-3 w-px" />
            <span>{runDurationLabel(run)}</span>
          </div>
        </div>

        <RunResult run={run} />
      </div>
    </PageContent>
  );
}

function BackToActivity() {
  return (
    <Link
      to="/actions/your-actions/activity"
      className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm transition-colors"
    >
      <ArrowLeftIcon className="size-4" />
      Activity Log
    </Link>
  );
}
